import { millisecondsInDay } from 'date-fns/constants';
import { useMemo } from 'react';
import { MAX_ORDER_EXPIRY_BUFFER_MS, MAX_ORDER_EXPIRY_DAYS } from '../../../constants';
import { OrderSide } from '../../../types/generated';
import { RawOrderFormData } from '../../../types/orders';
import { useTradeFormStore } from '../TradeForm/TradeFormState';

// Construct an OrderFormData object from the trade form store, so that it can
// be passed directly into `validateOrderFormData`, to avoid duplication
// between trade form validation and trade form submission.
export const useOrderFormData = (): RawOrderFormData => {
  const type = useTradeFormStore((s) => s.orderType);
  const price = useTradeFormStore((s) => s.limitPrice);
  const side = useTradeFormStore((s) => s.side);
  const amount = useTradeFormStore((s) => s.amount);
  const timeInForce = useTradeFormStore((s) => s.tif);
  const postOnly = useTradeFormStore((s) => s.postOnly);
  const reduceOnly = useTradeFormStore((s) => s.reduceOnly);
  const expiryInDays = useTradeFormStore((s) => s.expiryInDays);
  const expiresFromNow =
    timeInForce === 'GTD' && type === 'LIMIT'
      ? // Ensure we are below max order expiry
        Math.min(
          expiryInDays * millisecondsInDay,
          MAX_ORDER_EXPIRY_DAYS * millisecondsInDay - MAX_ORDER_EXPIRY_BUFFER_MS
        )
      : undefined;
  const stopLoss = useTradeFormStore((s) => s.stopLoss);
  const takeProfit = useTradeFormStore((s) => s.takeProfit);

  return useMemo(
    () => ({
      type,
      side: side === 'long' ? OrderSide.BUY : OrderSide.SELL,
      price: type === 'LIMIT' ? price || 0n : undefined,
      timeInForce: type === 'LIMIT' ? timeInForce : undefined,
      postOnly: type === 'LIMIT' ? postOnly : undefined,
      close: false,
      amount: amount || 0n,
      reduceOnly,
      expiresFromNow,
      stopLoss: type === 'LIMIT' ? stopLoss : undefined,
      takeProfit: type === 'LIMIT' ? takeProfit : undefined,
    }),
    [
      type,
      side,
      price,
      timeInForce,
      postOnly,
      amount,
      reduceOnly,
      expiresFromNow,
      stopLoss,
      takeProfit,
    ]
  );
};
